import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MessageCircle, Plus, ChevronDown, ChevronUp, Play, Video } from 'lucide-react'
import { SidebarSection } from './sidebar-section'
import { SidebarItem } from './sidebar-item'
import { Button } from '@/components/ui/button'
import { chatService, ConversationListItem } from '@/services/chat-service'
import { cn } from '@/lib/utils'

interface CreatorCopilotSectionProps {
  sidebarCollapsed: boolean
  onItemClick: () => void
}

export function CreatorCopilotSection({ sidebarCollapsed, onItemClick }: CreatorCopilotSectionProps) {
  const navigate = useNavigate()
  const [chatExpanded, setChatExpanded] = useState(false)
  const [conversations, setConversations] = useState<ConversationListItem[]>([])
  const [loadingConversations, setLoadingConversations] = useState(false)

  const fetchConversations = async () => {
    if (loadingConversations) return
    
    setLoadingConversations(true)
    try {
      const response = await chatService.getUserConversations()
      if (response.success) {
        setConversations(response.conversations)
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error)
    } finally {
      setLoadingConversations(false)
    }
  }

  const handleChatToggle = () => {
    const newExpanded = !chatExpanded
    setChatExpanded(newExpanded)
    
    if (newExpanded && conversations.length === 0) {
      fetchConversations()
    }
  }

  const handleConversationClick = (conversationId: string) => {
    navigate(`/chat/${conversationId}`)
    onItemClick()
  }

  const handleNewChatClick = () => {
    navigate('/chat')
    onItemClick()
  }

  if (sidebarCollapsed) {
    return (
      <SidebarSection
        title="Creator Copilot"
        sidebarCollapsed={sidebarCollapsed}
      >
        <SidebarItem
          name="Contextual Chat"
          href="/chat"
          icon={MessageCircle}
          sidebarCollapsed={sidebarCollapsed}
          onClick={onItemClick}
        />
        <SidebarItem
          name="Playground"
          href="/playground"
          icon={Play}
          sidebarCollapsed={sidebarCollapsed}
          onClick={onItemClick}
        />
        <SidebarItem
          name="Shorts Narrator"
          href="/shorts-narrator"
          icon={Video}
          sidebarCollapsed={sidebarCollapsed}
          onClick={onItemClick}
        />
      </SidebarSection>
    )
  }

  return (
    <SidebarSection
      title="Creator Copilot"
      icon={<MessageCircle className="h-4 w-4" />}
      sidebarCollapsed={sidebarCollapsed}
      defaultOpen={true}
    >
      <div className="space-y-1">
        {/* Main Chat Item */}
        <div
          className={cn(
            "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
            "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          )}
        >
          <MessageCircle className="h-5 w-5 flex-shrink-0" />
          <span 
            className="flex-1 cursor-pointer"
            onClick={handleNewChatClick}
          >
            Contextual Chat
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 hover:bg-accent/50"
            onClick={handleChatToggle}
          >
            {chatExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Expanded Chat List */}
        {chatExpanded && (
          <div className="ml-8 space-y-1">
            {/* New Chat Button */}
            <div
              className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-accent hover:text-accent-foreground rounded-lg transition-colors text-muted-foreground"
              onClick={handleNewChatClick}
            >
              <Plus className="h-3 w-3" />
              <span>New Conversation</span>
            </div>

            {/* Loading State */}
            {loadingConversations && (
              <div className="px-3 py-2 text-xs text-muted-foreground">
                Loading conversations...
              </div>
            )}

            {/* No Conversations */}
            {!loadingConversations && conversations.length === 0 && (
              <div className="px-3 py-2 text-xs text-muted-foreground">
                No conversations yet
              </div>
            )}

            {/* Conversation List */}
            {conversations.map((conversation) => (
              <div
                key={conversation.conversation_id}
                className="px-3 py-2 text-xs cursor-pointer hover:bg-accent hover:text-accent-foreground rounded-lg transition-colors truncate"
                onClick={() => handleConversationClick(conversation.conversation_id)}
                title={conversation.title || `Conversation ${conversation.conversation_id}`}
              >
                {conversation.title || `Conversation ${conversation.conversation_id.slice(0, 8)}...`}
              </div>
            ))}
          </div>
        )}

        {/* Additional Creator Tools */}
        <SidebarItem
          name="Playground"
          href="/playground"
          icon={Play}
          sidebarCollapsed={false}
          onClick={onItemClick}
        />
        
        <SidebarItem
          name="Shorts Narrator"
          href="/shorts-narrator"
          icon={Video}
          sidebarCollapsed={false}
          onClick={onItemClick}
        />
      </div>
    </SidebarSection>
  )
}
